import { Component, computed, inject, input } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { KENDO_TABSTRIP } from "@progress/kendo-angular-layout";
import { Author } from "../models/author.model";
import { Flight } from "../models/flight.model";
import { FlightImagesComponent } from "../flight-images/flight-images.component";
import { LogDatePipe, ZonedDateTimePipe } from "../flights2.pipes";
import { BootstrapService } from "../../shared/bootstrap/bootstrap.service";

type DetailTab = "enginePowerCheck" | "penaltyFactor" | "notes" | "refuel" | "oil" | "crew" | "operations" | "specifications" | "typeNotes" | "images";

/** A defect, a remark or an action taken, as one row of the notes tab. */
interface NoteRow {
    id: number;
    column: "defect" | "remark" | "actionTaken";
    text: string | null;
    date: string | null;
    signedBy: Author | null;
    writtenAsRemark: boolean;
}

@Component({
    selector: "app-flight-detail",
    imports: [TranslatePipe, KENDO_TABSTRIP, LogDatePipe, ZonedDateTimePipe, FlightImagesComponent],
    templateUrl: "./flight-detail.component.html",
    styleUrl: "./flight-detail.component.scss",
})
export class FlightDetailComponent {
    private readonly bootstrap = inject(BootstrapService);

    readonly flight = input.required<Flight>();
    /** The time zone the moments logged with the flight are shown in. */
    readonly timeZone = input.required<string>();

    readonly notes = computed<NoteRow[]>(() => {
        const flight = this.flight();
        return [
            ...flight.defects.map<NoteRow>((defect) => ({
                id: defect.id,
                column: "defect",
                text: defect.report,
                date: defect.reportedDate,
                signedBy: defect.signedBy,
                writtenAsRemark: false,
            })),
            ...flight.remarks.map<NoteRow>((remark) => ({
                id: remark.id,
                column: "remark",
                text: remark.report,
                date: remark.reportedDate,
                signedBy: remark.signedBy,
                writtenAsRemark: false,
            })),
            ...flight.actionsTaken.map<NoteRow>((action) => ({
                id: action.id,
                column: "actionTaken",
                text: action.actionTaken,
                date: action.actionDate,
                signedBy: action.signedBy,
                writtenAsRemark: action.kind === "remark",
            })),
        ];
    });

    readonly counts = computed<Record<DetailTab, number>>(() => {
        const flight = this.flight();
        return {
            enginePowerCheck: flight.enginePowerCheck ? 1 : 0,
            penaltyFactor: flight.penaltyFactor ? 1 : 0,
            notes: this.notes().length,
            refuel: flight.refuelBeforeFirstFlight.length,
            oil: flight.oilSuppliedBeforeFlight.length,
            crew: flight.crewData.length,
            operations: flight.flightOperationIds.length,
            specifications: flight.flightSpecificationIds.length,
            typeNotes: flight.flightTypeNoteIds.length,
            images: flight.imagesCount,
        };
    });

    readonly lists = computed(() => {
        const flight = this.flight();
        const named = (ids: number[], names: Map<number, string>) => ids.map((id) => ({ id: id, name: names.get(id) ?? `#${id}` }));
        return [
            { tab: "operations" as const, items: named(flight.flightOperationIds, this.bootstrap.flightOperationNames()) },
            { tab: "specifications" as const, items: named(flight.flightSpecificationIds, this.bootstrap.flightSpecificationNames()) },
            { tab: "typeNotes" as const, items: named(flight.flightTypeNoteIds, this.bootstrap.flightTypeNoteNames()) },
        ];
    });

    readonly selected = computed<DetailTab>(() => {
        const counts = this.counts();
        const tabs: DetailTab[] = ["enginePowerCheck", "penaltyFactor", "notes", "refuel", "oil", "crew", "operations", "specifications", "typeNotes"];
        return tabs.find((tab) => counts[tab] > 0) ?? "enginePowerCheck";
    });
}
